/**
 * Markdown Library - Formatters
 * 
 * Pure functions for formatting and transforming content.
 * All functions are stateless and easily testable.
 */

import type { CellFormatResult, CellType } from './types';
import {
    BRAZILIAN_CURRENCY_PATTERN,
    BRAZILIAN_NUMBER_WITH_THOUSANDS,
    BRAZILIAN_DECIMAL,
    INTEGER_PATTERN,
    PERCENTAGE_WITH_THOUSANDS,
    SIMPLE_PERCENTAGE,
    NUMERIC_HEADER_KEYWORDS,
} from './patterns';

// =============================================================================
// Cell Content Detection & Formatting
// =============================================================================

/**
 * Detects the type of content in a table cell and returns formatted value.
 * 
 * @param content - Raw cell content
 * @returns Object with formatted string and detected type
 */
export function formatCellContent(content: string): CellFormatResult {
    const trimmed = content.trim();

    // Check for Brazilian currency (R$ prefix or implied)
    if (BRAZILIAN_CURRENCY_PATTERN.test(trimmed) || BRAZILIAN_NUMBER_WITH_THOUSANDS.test(trimmed)) {
        // If it has R$, it's definitely currency
        if (trimmed.startsWith('R$')) {
            return { formatted: trimmed, type: 'currency' };
        }
        // Large numbers with thousands separator are likely currency
        if (BRAZILIAN_NUMBER_WITH_THOUSANDS.test(trimmed)) {
            return { formatted: trimmed, type: 'currency' };
        }
    }

    // Check for percentage
    if (PERCENTAGE_WITH_THOUSANDS.test(trimmed) || SIMPLE_PERCENTAGE.test(trimmed)) {
        return { formatted: trimmed, type: 'percentage' };
    }

    // Check for numbers (Brazilian format)
    if (BRAZILIAN_DECIMAL.test(trimmed) || INTEGER_PATTERN.test(trimmed)) {
        return { formatted: trimmed, type: 'number' };
    }

    return { formatted: trimmed, type: 'text' };
}

/**
 * Checks if a header name indicates numeric content.
 * Used to determine column alignment.
 * 
 * @param header - Header text
 * @returns True if the header suggests numeric content
 */
export function isNumericHeader(header: string): boolean {
    const lower = header.toLowerCase().trim();
    return NUMERIC_HEADER_KEYWORDS.some(keyword => lower.includes(keyword));
}

// =============================================================================
// Markdown Processing
// =============================================================================

/**
 * Strips markdown formatting from text.
 * Useful for exporting clean data to Excel.
 * 
 * @param text - Text with markdown formatting
 * @returns Clean text without markdown
 */
export function stripMarkdown(text: string): string {
    return text
        .replace(/\*\*/g, '')  // Bold
        .replace(/\*/g, '')    // Italic
        .replace(/`/g, '')     // Code
        .replace(/~~/g, '')    // Strikethrough
        .replace(/__/g, '')    // Alt bold
        .replace(/_/g, '')     // Alt italic
        .trim();
}

/**
 * Converts inline markdown to HTML.
 * Handles: **bold**, *italic*, `code`, ~~strikethrough~~
 * 
 * @param text - Text with inline markdown
 * @returns HTML string
 */
export function processInlineMarkdown(text: string): string {
    let result = text;

    // LaTeX symbol map - comprehensive list
    const latexSymbols: Record<string, string> = {
        // Arrows
        '\\rightarrow': '→',
        '\\leftarrow': '←',
        '\\leftrightarrow': '↔',
        '\\Rightarrow': '⇒',
        '\\Leftarrow': '⇐',
        '\\Leftrightarrow': '⇔',
        '\\uparrow': '↑',
        '\\downarrow': '↓',
        '\\updownarrow': '↕',
        '\\nearrow': '↗',
        '\\searrow': '↘',
        '\\nwarrow': '↖',
        '\\swarrow': '↙',
        '\\longrightarrow': '⟶',
        '\\longleftarrow': '⟵',
        '\\mapsto': '↦',

        // Greek letters (lowercase)
        '\\alpha': 'α',
        '\\beta': 'β',
        '\\gamma': 'γ',
        '\\delta': 'δ',
        '\\epsilon': 'ε',
        '\\zeta': 'ζ',
        '\\eta': 'η',
        '\\theta': 'θ',
        '\\iota': 'ι',
        '\\kappa': 'κ',
        '\\lambda': 'λ',
        '\\mu': 'μ',
        '\\nu': 'ν',
        '\\xi': 'ξ',
        '\\pi': 'π',
        '\\rho': 'ρ',
        '\\sigma': 'σ',
        '\\tau': 'τ',
        '\\upsilon': 'υ',
        '\\phi': 'φ',
        '\\chi': 'χ',
        '\\psi': 'ψ',
        '\\omega': 'ω',

        // Greek letters (uppercase)
        '\\Alpha': 'Α',
        '\\Beta': 'Β',
        '\\Gamma': 'Γ',
        '\\Delta': 'Δ',
        '\\Epsilon': 'Ε',
        '\\Zeta': 'Ζ',
        '\\Eta': 'Η',
        '\\Theta': 'Θ',
        '\\Iota': 'Ι',
        '\\Kappa': 'Κ',
        '\\Lambda': 'Λ',
        '\\Mu': 'Μ',
        '\\Nu': 'Ν',
        '\\Xi': 'Ξ',
        '\\Pi': 'Π',
        '\\Rho': 'Ρ',
        '\\Sigma': 'Σ',
        '\\Tau': 'Τ',
        '\\Upsilon': 'Υ',
        '\\Phi': 'Φ',
        '\\Chi': 'Χ',
        '\\Psi': 'Ψ',
        '\\Omega': 'Ω',

        // Math operators
        '\\times': '×',
        '\\div': '÷',
        '\\pm': '±',
        '\\mp': '∓',
        '\\cdot': '·',
        '\\ast': '∗',
        '\\star': '⋆',
        '\\circ': '∘',
        '\\bullet': '•',

        // Comparisons
        '\\neq': '≠',
        '\\leq': '≤',
        '\\geq': '≥',
        '\\le': '≤',
        '\\ge': '≥',
        '\\ll': '≪',
        '\\gg': '≫',
        '\\approx': '≈',
        '\\sim': '∼',
        '\\equiv': '≡',
        '\\propto': '∝',

        // Sets and logic
        '\\in': '∈',
        '\\notin': '∉',
        '\\subset': '⊂',
        '\\supset': '⊃',
        '\\subseteq': '⊆',
        '\\supseteq': '⊇',
        '\\cup': '∪',
        '\\cap': '∩',
        '\\emptyset': '∅',
        '\\exists': '∃',
        '\\forall': '∀',
        '\\neg': '¬',
        '\\land': '∧',
        '\\lor': '∨',

        // Special symbols
        '\\infty': '∞',
        '\\partial': '∂',
        '\\nabla': '∇',
        '\\sum': '∑',
        '\\prod': '∏',
        '\\int': '∫',
        '\\sqrt': '√',
        '\\checkmark': '✓',
        '\\dagger': '†',
        '\\ddagger': '‡',
        '\\degree': '°',
        '\\prime': '′',
        '\\ldots': '…',
        '\\cdots': '⋯',
    };

    // Replace LaTeX symbols - both with and without $ delimiters
    for (const [latex, unicode] of Object.entries(latexSymbols)) {
        // With $ delimiters: $\rightarrow$
        const withDelimiters = new RegExp(`\\$${latex.replace(/\\/g, '\\\\')}\\$`, 'g');
        result = result.replace(withDelimiters, unicode);

        // Without $ delimiters: \rightarrow (but not inside other words)
        const withoutDelimiters = new RegExp(`(?<![a-zA-Z])${latex.replace(/\\/g, '\\\\')}(?![a-zA-Z])`, 'g');
        result = result.replace(withoutDelimiters, unicode);
    }

    // Handle remaining inline LaTeX that wasn't matched (just remove the $ delimiters)
    result = result.replace(/\$([^$]+)\$/g, '$1');

    // Bold: **text** or __text__
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
    result = result.replace(/__([^_]+)__/g, '<strong class="font-semibold">$1</strong>');

    // Italic: *text* or _text_ (but not part of **)
    result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>');
    result = result.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em class="italic">$1</em>');

    // Code: `code`
    result = result.replace(
        /`([^`]+)`/g,
        '<code class="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>'
    );

    // Strikethrough: ~~text~~
    result = result.replace(/~~([^~]+)~~/g, '<del class="line-through opacity-60">$1</del>');

    return result;
}



// =============================================================================
// Currency Symbol Escaping (for LaTeX compatibility)
// =============================================================================

/**
 * Escapes Brazilian currency symbols to prevent LaTeX parsing issues.
 * R$ conflicts with LaTeX $ delimiters.
 * 
 * @param text - Text potentially containing R$
 * @returns Text with escaped currency symbols
 */
export function escapeCurrencyForLatex(text: string): string {
    // Normalize: R\$ or R\\$ -> R$
    let clean = text.replace(/R\\+\$/g, 'R$');
    // Escape for ReactMarkdown: R$ -> R\$
    return clean.replace(/R\$/g, 'R\\$');
}

// =============================================================================
// Alignment Utilities
// =============================================================================

/**
 * Gets CSS class for text alignment.
 * 
 * @param align - Alignment direction
 * @returns Tailwind CSS class
 */
export function getAlignmentClass(align: 'left' | 'center' | 'right'): string {
    const alignmentMap = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    };
    return alignmentMap[align];
}

/**
 * Gets CSS classes for a cell based on its content type.
 * 
 * @param type - Detected cell type
 * @returns Tailwind CSS classes
 */
export function getCellTypeClasses(type: CellType): string {
    const baseClasses = 'text-sm';

    switch (type) {
        case 'currency':
            return `${baseClasses} font-mono font-medium text-accent text-right`;
        case 'percentage':
            return `${baseClasses} font-mono font-medium text-blue-600 dark:text-blue-400 text-right`;
        case 'number':
            return `${baseClasses} font-mono font-medium text-slate-700 dark:text-slate-300 text-right`;
        case 'text':
        default:
            return `${baseClasses} text-slate-700 dark:text-slate-300`;
    }
}
